import { inject, Injectable, InjectionToken, signal } from '@angular/core';

import { DiagnosticVerbosity, ShutdownReason, StorageFailure } from './enums';
import { SessionRecordModel } from './models';
import { TelemetryScrubberService } from './telemetry-scrubber.service';

export const TELEMETRY_STORAGE = new InjectionToken<() => Storage | undefined>('Telemetry storage', {
  providedIn: 'root',
  factory: () => (): Storage | undefined => typeof localStorage === 'undefined' ? undefined : localStorage
});
export const DISPLAY_ID_KEY: string = 'ds.telemetry.display-id.v1';
export const SESSION_KEY: string = 'ds.telemetry.session.v1';
export const VERBOSITY_KEY: string = 'ds.telemetry.verbosity.v1';

// Carries identity and last session across a reload — how an unclean shutdown is detected — and re-validates everything it reads back.
@Injectable({ providedIn: 'root' })
export class SessionStoreService {
  private readonly storageFactory = inject(TELEMETRY_STORAGE);
  private readonly scrubber = inject(TelemetryScrubberService);
  private readonly failure = signal<StorageFailure | null>(null);
  private cachedDisplayId?: string;

  public readonly storageFailure = this.failure.asReadonly();

  public createTelemetryId(): string {
    const bytes = new Uint8Array(16);

    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index++) {
        bytes[index] = Math.floor(Math.random() * 256);
      }
    }

    // The version and variant bits that make this a valid UUIDv4, which is what the scrubber's id check enforces.
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  public sanitizeSession(value: unknown): SessionRecordModel | undefined {
    const item = this.scrubber.record(value);

    if (item['schema'] !== 1 || !this.scrubber.isTelemetryId(item['displayId'])
      || !this.scrubber.isTelemetryId(item['sessionId']) || typeof item['closedCleanly'] !== 'boolean'
      || !this.isValidTimestamp(item['startedAt']) || !this.isValidTimestamp(item['updatedAt'])) {
      return undefined;
    }

    const reason = this.scrubber.scrub({ reason: item['reason'] })['reason'];

    return {
      schema: 1, displayId: item['displayId'], sessionId: item['sessionId'],
      startedAt: item['startedAt'], updatedAt: item['updatedAt'], closedCleanly: item['closedCleanly'],
      reason: this.validShutdownReason(reason),
      snapshot: this.scrubber.scrub(item['snapshot']),
      breadcrumbs: this.scrubber.scrubBreadcrumbs(Array.isArray(item['breadcrumbs']) ? item['breadcrumbs'] : [])
    };
  }

  public displayId(): string {
    if (!this.cachedDisplayId) {
      const persisted = this.read(DISPLAY_ID_KEY);

      if (persisted !== null && !this.scrubber.isTelemetryId(persisted)) {
        this.failure.set(StorageFailure.Invalid);
      }

      this.cachedDisplayId = this.scrubber.isTelemetryId(persisted) ? persisted : this.createTelemetryId();

      if (persisted !== this.cachedDisplayId) {
        this.write(DISPLAY_ID_KEY, this.cachedDisplayId);
      }
    }

    return this.cachedDisplayId;
  }

  // Survives the nightly reload, so a remote diagnostics push does not evaporate within a day.
  public readVerbosity(): DiagnosticVerbosity | undefined {
    const persisted = this.read(VERBOSITY_KEY);

    if (persisted === null) {
      return undefined;
    }

    if (!Object.values(DiagnosticVerbosity).includes(persisted as DiagnosticVerbosity)) {
      this.failure.set(StorageFailure.Invalid);

      return undefined;
    }

    return persisted as DiagnosticVerbosity;
  }

  public writeVerbosity(level: DiagnosticVerbosity): boolean {
    return this.write(VERBOSITY_KEY, level);
  }

  public readSession(): SessionRecordModel | undefined {
    const serialized = this.read(SESSION_KEY);

    if (!serialized) {
      return undefined;
    }

    try {
      const parsed: unknown = serialized.length <= 300_000 ? JSON.parse(serialized) : undefined;
      const session = this.sanitizeSession(parsed);

      if (!session) {
        this.failure.set(StorageFailure.Invalid);
      }

      return session;
    } catch {
      this.failure.set(StorageFailure.Invalid);

      return undefined;
    }
  }

  // Sanitised on the way out as well as in: a record that could not be read back is not worth storing.
  public writeSession(session: SessionRecordModel): boolean {
    const sanitized = this.sanitizeSession(session);

    if (!sanitized) {
      this.failure.set(StorageFailure.Invalid);

      return false;
    }

    return this.write(SESSION_KEY, JSON.stringify(sanitized));
  }

  private read(key: string): string | null {
    try {
      const storage = this.storageFactory();

      if (!storage) {
        this.failure.set(StorageFailure.Unavailable);

        return null;
      }

      return storage.getItem(key);
    } catch {
      this.failure.set(StorageFailure.ReadFailed);

      return null;
    }
  }

  private write(key: string, value: string): boolean {
    try {
      const storage = this.storageFactory();

      if (!storage) {
        this.failure.set(StorageFailure.Unavailable);

        return false;
      }

      storage.setItem(key, value);

      return true;
    } catch {
      this.failure.set(StorageFailure.WriteFailed);

      return false;
    }
  }

  private validShutdownReason(value: unknown): ShutdownReason | undefined {
    return Object.values(ShutdownReason).includes(value as ShutdownReason) ? value as ShutdownReason : undefined;
  }

  private isValidTimestamp(value: unknown): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  }
}
